import { Box } from '@material-ui/core';
import { CashInCashOutMethod, CashInCashOutType } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';

import { AccountHolderParticipationLevelOptions } from '@/components/modules/BorrowerAccountDetails/constants/AccountHolderParticipationLevelOptions';
import { CashInCashOutTypeOptions } from '@/components/modules/BorrowerAccountDetails/constants/CashInCashOutTypeOptions';
import { getBankDetailsByAccountNumber } from '@/components/modules/BorrowerAccountDetails/utils/getBankDetailsByAccountNumber';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Font } from '@/components/shared/components/Font';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { Text } from '@/components/shared/components/Text';
import { useScreen } from '@/hooks/useScreen';

interface BankAccountDetailsFormProps {
  cashInCashOutMethod: CashInCashOutMethod;
  isEditable?: boolean;
}

export function BankAccountDetailsForm({ cashInCashOutMethod, isEditable }: BankAccountDetailsFormProps) {
  const { isMobile } = useScreen();
  const bankDetails = getBankDetailsByAccountNumber({ accountNumber: cashInCashOutMethod.accountNumber });

  const watch = useFormContext()?.watch;

  const cashInCashOutMethodWatcher = watch ? watch('type') : undefined;

  return (
    <Box display={isEditable && 'grid'} gridGap={'12px'}>
      {cashInCashOutMethodWatcher !== CashInCashOutType.FLYWIRE && (
        <TextFormField
          label={isMobile ? 'IBAN' : 'ACCOUNT_NUMBER'}
          value={cashInCashOutMethod.accountNumber}
          name={'accountNumber'}
          copyToClipboard
        />
      )}
      {isEditable && (
        <>
          <TextFormField
            label={'ACCOUNT_HOLDER_FIRST_NAME'}
            value={cashInCashOutMethod.accountHolderFirstName}
            name={'accountHolderFirstName'}
          />
          <TextFormField
            label={'ACCOUNT_HOLDER_FIRST_LAST_NAME'}
            value={cashInCashOutMethod.accountHolderFirstLastName}
            name={'accountHolderFirstLastName'}
          />
          <TextFormField
            label={'ACCOUNT_HOLDER_SECOND_LAST_NAME'}
            value={cashInCashOutMethod.accountHolderSecondLastName}
            name={'accountHolderSecondLastName'}
          />
          <SelectFormField
            label={'ACCOUNT_HOLDER_PARTICIPATION_LEVEL'}
            value={cashInCashOutMethod.accountHolderParticipationLevel}
            name={'accountHolderParticipationLevel'}
            selectOptions={AccountHolderParticipationLevelOptions}
          />
          <TextFormField label={'DESCRIPTION'} value={cashInCashOutMethod.description} name={'description'} />
          <SelectFormField
            label={'CASH_IN_CASH_OUT_METHOD_TYPE'}
            value={cashInCashOutMethod.type}
            name={'type'}
            selectOptions={CashInCashOutTypeOptions}
          />
        </>
      )}
      {!isEditable && bankDetails && (
        <Box display={'flex'} flexDirection={'column'} py={'6px'} width={'100%'}>
          <Box pb={'6px'} flexGrow={1} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Font className={'overflow-text-ellipsis'} variant="subtitle2" color="textSecondary">
              <Text tId={'BANK_NAME'} />
            </Font>
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} pb={'2px'}>
              <Font variant="h5" color="textPrimary" style={{ textAlign: 'right', paddingLeft: '8px' }}>
                {bankDetails.NAME}
              </Font>
              {bankDetails.LENDER_NAME && (
                <LenderIcon lenderName={bankDetails.LENDER_NAME} style={{ marginLeft: 6, paddingBottom: '5px' }} />
              )}
            </Box>
          </Box>
          <CustomDivider margin={0} />
        </Box>
      )}
    </Box>
  );
}
